import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BaseResponse, BCUser } from '@core/interfaces';
import { IBM_SSO_CONFIG } from '@core/providers';
import { generateRandomString } from '@shared/utilities';
import { Observable, switchMap, tap } from 'rxjs';
import { ApiEndpoints } from 'src/app/shared/common/apiPoint';
import { PermissionsList } from '../../shared/common/permissionsList.enum';
import { User } from '../../shared/interfaces/user.interface';
import { LocalStorageService } from './local-storage.service';
import { environment } from '@envs/environment';
import { LOG_IN_ROUTE } from '@shared/common/constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(IBM_SSO_CONFIG);
  private readonly _localStorageService = inject(LocalStorageService);
  private readonly _router = inject(Router);

  readonly userDataKey = 'currentUser';
  currentUser = signal<BCUser | null>(null);
  showAuthDataSitterDialog = signal(false);
  jwtHelper = new JwtHelperService();

  constructor() {
    this.initializeAuth();
  }

  public checkAccess(pagename: string): boolean {
    const userinfoJSON = localStorage.getItem(this.userDataKey);

    if (userinfoJSON) {
      const userinfo = JSON.parse(userinfoJSON) as User;

      return userinfo.PermissionList.includes(
        PermissionsList[pagename as keyof typeof PermissionsList],
      );
    }

    return false;
  }

  checkIfLoggedIn() {
    if (!localStorage.getItem(this.userDataKey)) {
      return false;
    }
    const currentUser = JSON.parse(localStorage.getItem(this.userDataKey)!);
    const token = currentUser.token;

    if (token && !this.jwtHelper.isTokenExpired(token)) {
      // Token is present and not expired
      return true;
    }
    return false;
  }

  public loggedIn() {
    return !!localStorage.getItem(this.userDataKey);
  }

  public initSSOLogin() {
    return new Observable<Record<string, string>>(observer => {
      const state = generateRandomString(32);
      const nonce = generateRandomString(32);

      // Store state and nonce for verification
      sessionStorage.setItem('bc_oauth_state', state);
      sessionStorage.setItem('bc_oauth_nonce', nonce);

      const authUrl = `${this._config.isvOidcBaseUrl}/authorize?${this._prepareAuthParams(state, nonce)}`;
      const popup = this._openPopupWindow(authUrl);
      if (!popup) {
        observer.error(
          new Error('Popup blocked. Please allow popups for this site.'),
        );
        return;
      }

      // Listen for popup messages
      const messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (event.data.type === 'IBM_SSO_AUTH_SUCCESS') {
          window.removeEventListener('message', messageListener);
          popup.close();
          observer.next(event.data);
          observer.complete();
        } else if (event.data.type === 'IBM_SSO_AUTH_ERROR') {
          window.removeEventListener('message', messageListener);
          popup.close();
          observer.error(new Error(event.data.error));
        }
      };

      window.addEventListener('message', messageListener);

      // Handle popup closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          observer.error(new Error('Authentication cancelled'));
        }
      }, 1000);

      // Timeout after 5 minutes
      setTimeout(() => {
        if (!popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          popup.close();
          observer.error(new Error('Authentication timeout'));
        }
      }, 300000);
    });
  }

  public logout(): void {
    this._localStorageService.removeItem(this.userDataKey);
    sessionStorage.removeItem('bc_oauth_state');
    sessionStorage.removeItem('bc_oauth_nonce');
    this._router.navigate([LOG_IN_ROUTE]);
  }

  public handleSSOCallback(code: string, state: string) {
    // Verify state
    const storedState = sessionStorage.getItem('bc_oauth_state');
    if (state !== storedState) {
      throw new Error('Invalid state parameter');
    }

    return this._exchangeCodeForToken(code).pipe(
      switchMap(res => this._getUserProfile(res.access_token)),
      tap(res => {
        this.storeUserData(res.data);
        this.currentUser.set(res.data);
        sessionStorage.removeItem('bc_oauth_state');
        sessionStorage.removeItem('bc_oauth_nonce');
      }),
    );
  }

  /**
   * save user data to local storage with selected regions and locations
   */
  storeUserData(userinfo: BCUser) {
    this._localStorageService.setItem(this.userDataKey, userinfo);

    const regions = userinfo.regions;
    regions.sort((a, b) => a.name.localeCompare(b.name));
    this._localStorageService.setItem('selectedRegionsIds', [regions[0].id]);

    const locations = userinfo.locations;
    const filteredLocations = locations
      .filter(location => location.regionId === regions[0].id)
      .map(location => location.id);
    this._localStorageService.setItem('selectedLocationIds', [
      filteredLocations[0],
    ]);
  }

  /**
   * Get an authorized user from Local Storage and watch changes on value
   * @private
   */
  private initializeAuth() {
    this._localStorageService
      .watchKeyWithInitialValue<BCUser>(this.userDataKey)
      .subscribe(user => {
        this.currentUser.set(user);
        if (
          !user?.token &&
          environment.developmentMode &&
          !environment.isStandalonePortal
        ) {
          this.showAuthDataSitterDialog.set(true);
        }
      });
  }

  private _getUserProfile(token: string) {
    return this._http.post<BaseResponse<BCUser>>(
      ApiEndpoints.Identity.Authenticate,
      null,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json; charset=UTF-8',
        },
      },
    );
  }

  private _exchangeCodeForToken(auth_code: string) {
    const body = new URLSearchParams({
      code: auth_code,
      grant_type: 'authorization_code',
      client_id: this._config.clientId,
      client_secret: this._config.clientSecret,
      redirect_uri: `${this._config.redirectUri}/authentication/login/callback`,
    });
    return this._http.post<{ access_token: string }>(
      `${this._config.isvOidcBaseUrl}/token`,
      body.toString(),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );
  }

  private _openPopupWindow(url: string) {
    return window.open(url, '_blank', this._preparePopupWindow());
  }

  /**
   * Prepares the configuration string for opening a centered popup window with specified dimensions.
   * @return {string} A formatted string containing window features such as dimensions, position, and additional settings.
   */
  private _preparePopupWindow(): string {
    const popUpWidth = 601; // 440
    const popUpHeight = 720;
    const dualScreenLeft = window.screenLeft ?? window.screenX;
    const dualScreenTop = window.screenTop ?? window.screenY;

    const screenWidth =
      window.innerWidth || document.documentElement.clientWidth || screen.width;
    const screenHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      screen.height;

    const systemZoom = screenWidth / window.screen.availWidth;
    const left = (screenWidth - popUpWidth) / 2 / systemZoom + dualScreenLeft;
    const top = (screenHeight - popUpHeight) / 2 / systemZoom + dualScreenTop;
    return `popup,location=0,scrollbars=yes,status=yes,width=${popUpWidth},height=${popUpHeight},left=${left},top=${top}`;
  }

  private _prepareAuthParams(state: string, nonce: string): string {
    const params = new URLSearchParams({
      client_id: this._config.clientId,
      redirect_uri: `${this._config.redirectUri}/authentication/login/callback`,
      response_type: this._config.responseType,
      scope: this._config.scope ?? '',
      state: state,
      nonce: nonce,
    });
    return params.toString();
  }
}
